import { useTranslation } from 'react-i18next';
import { type Estimate, type EstimateEvidence } from '@apm/contracts';

/**
 * Một `Estimate` hiện ở **một** chỗ duy nhất — APM-AI-y
 * (`plan-ai-features.md` §0.4).
 *
 * ## Dẫn chứng trước, con số sau
 *
 * Bố cục cố ý đặt căn cứ ngay dưới con số, không giấu sau một nút "xem chi
 * tiết". Người duyệt đọc dẫn chứng rồi mới quyết định tin con số tới đâu; giấu
 * dẫn chứng đi là biến một ước lượng có căn cứ thành một lời phán.
 *
 * ## Mức tin cậy là của hệ thống, không phải của model
 *
 * `confidenceNote` do `capConfidence` điền khi mức model đề nghị bị hạ. Hiện nó
 * cạnh mức tin cậy chứ không phải ở cuối: "trung bình" đứng một mình đọc như
 * một đánh giá, còn "trung bình — chỉ có 2 việc tương tự" đọc như một sự thật
 * kiểm được.
 */
export function EstimateCard({
  estimate,
  onOpenTask,
  className,
}: {
  estimate: Estimate;
  /**
   * Mở task được trỏ tới làm dẫn chứng. Bỏ trống thì dẫn chứng là chữ thường.
   *
   * Không tự dựng đường dẫn ở đây: `find_similar_tasks` đọc **xuyên dự án**
   * (APM-AI-f), nên một `ref` hoàn toàn có thể trỏ tới task ở dự án khác — và
   * một liên kết gãy còn tệ hơn không có liên kết.
   */
  onOpenTask?: (taskId: string) => void;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <div className={`flex flex-col gap-1 text-xs ${className ?? ''}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-medium tabular-nums">
          {t('ai.estimate.value', {
            value: estimate.value,
            unit: t(`ai.estimate.unit.${estimate.unit}`),
          })}
        </span>
        {estimate.range ? (
          <span className="tabular-nums text-muted-foreground">
            {t('ai.estimate.range', { low: estimate.range.low, high: estimate.range.high })}
          </span>
        ) : null}
        <span className={confidenceClass(estimate.confidence)}>
          {t(`ai.estimate.confidence.${estimate.confidence}`)}
        </span>
      </div>

      {estimate.confidenceNote ? (
        <p className="text-muted-foreground">{estimate.confidenceNote}</p>
      ) : null}

      <p className="text-muted-foreground">{estimate.rationale}</p>

      <ul className="flex flex-col gap-0.5">
        {estimate.evidence.map((item, index) => (
          <li key={`${item.kind}-${item.ref ?? index}`} className="flex items-start gap-1.5">
            <span className="shrink-0 text-muted-foreground">
              {t(`ai.estimate.evidenceKind.${item.kind}`)}
            </span>
            {renderEvidence(item, onOpenTask)}
            {item.weight === 'primary' ? (
              <span className="shrink-0 text-muted-foreground">{t('ai.estimate.primary')}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function renderEvidence(item: EstimateEvidence, onOpenTask?: (taskId: string) => void) {
  if (item.kind === 'similar_task' && item.ref !== undefined && onOpenTask) {
    return (
      <button
        type="button"
        className="min-w-0 flex-1 text-left underline underline-offset-2 hover:no-underline"
        onClick={() => onOpenTask(item.ref!)}
      >
        {item.summary}
      </button>
    );
  }
  return <span className="min-w-0 flex-1">{item.summary}</span>;
}

/**
 * Bảng lớp viết sẵn, không ghép chuỗi lúc chạy.
 *
 * Tailwind quét **mã nguồn** để biết sinh lớp nào, nên `text-${x}-600` ghép lúc
 * chạy cho ra chữ trong suốt — đúng cạm bẫy đã ghi ở CLAUDE.md.
 */
function confidenceClass(confidence: Estimate['confidence']): string {
  const base = 'shrink-0 rounded-DEFAULT px-1.5 py-0.5 text-[11px] font-medium';
  switch (confidence) {
    case 'high':
      return `${base} bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-100`;
    case 'medium':
      return `${base} bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-100`;
    default:
      return `${base} bg-muted text-muted-foreground`;
  }
}
